/**
 * ProPortfolioCard — vendor uploads images + ≤30s videos with AI moderation.
 *
 * For professionals and entertainment-pro vendors (DJ / Nathaswaram). Up to
 * 10 images + 3 videos. Server runs Gemini Vision text-detection: any caption
 * or text overlay → upload rejected. Videos > 30s also rejected.
 *
 * Instagram/Facebook social-sync is wired via a "Connect" button that hits the
 * Meta integration (`/api/meta/login-url` → OAuth → `/api/meta/portfolio/import`).
 * Button label flips automatically based on the vendor's connection state.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Image as ImgIcon, VideoCamera, Trash, UploadSimple, InstagramLogo,
  Warning, Sparkle, Spinner, Play, MapPin, Star,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fullUrl = (p) => (p && p.startsWith('http') ? p : `${BACKEND_URL}${p || ''}`);

const ProPortfolioCard = () => {
  const [data, setData] = useState({
    items: [],
    image_count: 0,
    video_count: 0,
    max_images: 10,
    max_videos: 3,
    max_video_seconds: 30,
  });
  const [eligible, setEligible] = useState(true);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingVid, setUploadingVid] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Mirrors `/api/meta/connection-status` so we can flip the button label
  // between "Connect Instagram" (no token yet) and "Import from Instagram"
  // (already connected — clicking triggers a portfolio import).
  const [metaStatus, setMetaStatus] = useState({ configured: false, connected: false });
  const imgRef = useRef(null);
  const vidRef = useRef(null);

  useEffect(() => { load(); loadMetaStatus(); }, []); // eslint-disable-line

  const loadMetaStatus = async () => {
    try {
      const { data: s } = await axios.get(`${BACKEND_URL}/api/meta/connection-status`, { withCredentials: true });
      setMetaStatus(s || {});
    } catch (_) {
      // 403/401/503 — vendor not eligible or Meta not configured. Button stays
      // in "Connect" mode and the server will return a useful error on click.
    }
  };

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${BACKEND_URL}/api/me/portfolio`, { withCredentials: true });
      setData(d);
      setEligible(true);
    } catch (err) {
      // 403 → role/category not eligible (e.g. server/waiter, customer, admin).
      // Hide the card entirely so we don't show an empty/broken UI.
      if (err?.response?.status === 403) setEligible(false);
    }
  };

  const onImageChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return; }
    setUploadingImg(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      await axios.post(`${BACKEND_URL}/api/me/portfolio/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Image added to your portfolio');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setUploadingImg(false);
    if (imgRef.current) imgRef.current.value = '';
  };

  const onVideoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) { toast.error('Video must be under 50 MB'); return; }
    // Quick client-side duration check to avoid waiting for server moderation if obviously too long.
    try {
      const url = URL.createObjectURL(f);
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.src = url;
      const dur = await new Promise((res, rej) => {
        v.onloadedmetadata = () => res(v.duration);
        v.onerror = () => rej(new Error('bad video'));
        setTimeout(() => rej(new Error('timeout')), 8000);
      }).catch(() => 0);
      URL.revokeObjectURL(url);
      if (dur > 0 && dur > data.max_video_seconds + 0.5) {
        toast.error(`Video is ${dur.toFixed(1)}s — must be ≤ ${data.max_video_seconds}s.`);
        if (vidRef.current) vidRef.current.value = '';
        return;
      }
    } catch {
      // Browser couldn't probe — let the server reject if it's actually too long.
    }
    setUploadingVid(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      await axios.post(`${BACKEND_URL}/api/me/portfolio/upload-video`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Video added to your portfolio');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setUploadingVid(false);
    if (vidRef.current) vidRef.current.value = '';
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete this ${item.kind}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/me/portfolio/${item.id}`, { withCredentials: true });
      toast.success('Removed');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove');
    }
  };

  const onSocialSync = async () => {
    setSyncing(true);
    try {
      // Connected → trigger a real portfolio import from IG + FB.
      if (metaStatus.connected) {
        const { data: r } = await axios.post(
          `${BACKEND_URL}/api/meta/portfolio/import`, {}, { withCredentials: true },
        );
        const imported = r?.imported ?? 0;
        const skipped = r?.skipped ?? 0;
        toast.success(`Imported ${imported} photos from Instagram/Facebook` +
          (skipped ? ` (${skipped} skipped — duplicates or limits)` : ''));
        load();
      } else {
        // Not connected → kick off the Meta OAuth flow in the same window.
        const { data: r } = await axios.get(
          `${BACKEND_URL}/api/meta/login-url`, { withCredentials: true },
        );
        if (r?.auth_url) {
          window.location.href = r.auth_url;
        } else {
          toast.error('Could not start Instagram connection. Try again.');
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 503) {
        toast.error('Instagram/Facebook is not configured on the server yet. Contact admin.');
      } else {
        toast.error(detail || 'Instagram action failed. Try again.');
      }
    }
    setSyncing(false);
  };

  const imgCap = data.max_images || 10;
  const vidCap = data.max_videos || 3;
  const imgAtCap = data.image_count >= imgCap;
  const vidAtCap = data.video_count >= vidCap;
  const imgItems = (data.items || []).filter((x) => x.kind === 'image');
  const vidItems = (data.items || []).filter((x) => x.kind === 'video');

  // Hide the card entirely for users the backend says aren't eligible
  // (servers / waiters, customers, admins).
  if (!eligible) return null;

  return (
    <Card className="bg-white border-[#E8E1D5] mt-6" data-testid="pro-portfolio-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ImgIcon size={20} className="text-brand-primary" weight="fill" /> Portfolio Gallery
        </CardTitle>
        <CardDescription>
          Build a gallery of <span className="font-semibold">up to {imgCap} photos</span> and{' '}
          <span className="font-semibold">{vidCap} videos (≤ {data.max_video_seconds}s)</span>. AI
          moderation auto-rejects anything with text, captions or watermarks — keep the focus on
          your work. Customers see this gallery under your name on Instant Pricing.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Upload + Social Sync buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
          <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/webp"
            onChange={onImageChange} className="hidden" data-testid="portfolio-image-file" />
          <Button onClick={() => imgRef.current?.click()} disabled={uploadingImg || imgAtCap}
            className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-50"
            data-testid="portfolio-add-image-btn">
            {uploadingImg ? <Spinner size={16} className="mr-2 animate-spin" /> : <UploadSimple size={16} className="mr-2" weight="bold" />}
            {uploadingImg ? 'Checking…' : imgAtCap ? `Photos full (${imgCap}/${imgCap})` : `Add Photo (${data.image_count}/${imgCap})`}
          </Button>

          <input ref={vidRef} type="file" accept="video/mp4,video/quicktime,video/webm"
            onChange={onVideoChange} className="hidden" data-testid="portfolio-video-file" />
          <Button onClick={() => vidRef.current?.click()} disabled={uploadingVid || vidAtCap}
            className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-50"
            data-testid="portfolio-add-video-btn">
            {uploadingVid ? <Spinner size={16} className="mr-2 animate-spin" /> : <VideoCamera size={16} className="mr-2" weight="fill" />}
            {uploadingVid ? 'Checking…' : vidAtCap ? `Videos full (${vidCap}/${vidCap})` : `Add Video (${data.video_count}/${vidCap})`}
          </Button>

          <Button onClick={onSocialSync} disabled={syncing}
            variant="outline"
            className="rounded-none border-pink-400 text-pink-600 hover:bg-pink-50 disabled:opacity-50"
            data-testid="portfolio-instagram-sync">
            {syncing ? <Spinner size={16} className="mr-2 animate-spin" /> : <InstagramLogo size={16} className="mr-2" weight="fill" />}
            {syncing
              ? (metaStatus.connected ? 'Importing…' : 'Connecting…')
              : (metaStatus.connected ? 'Import from Instagram' : 'Connect Instagram')}
          </Button>
        </div>

        {/* Google Maps URL import — gated on admin providing GOOGLE_MAPS_API_KEY. */}
        <GMapsImportRow onImported={load} />

        {(uploadingImg || uploadingVid) && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 border border-amber-200 text-xs text-amber-800 rounded">
            <Sparkle size={14} weight="fill" className="text-amber-600" />
            AI is checking for text/captions… we&apos;ll reject the upload if any are detected.
          </div>
        )}

        {/* Items grid */}
        {(imgItems.length + vidItems.length) === 0 ? (
          <div className="border-2 border-dashed border-[#E8E1D5] rounded p-6 text-center" data-testid="portfolio-empty">
            <ImgIcon size={32} className="text-text-secondary mx-auto" />
            <p className="text-sm text-text-secondary mt-2">No portfolio items yet — add your first photo or video above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {vidItems.map((item) => (
              <div key={item.id} className="relative aspect-square border border-[#E8E1D5] rounded overflow-hidden group bg-black" data-testid={`portfolio-item-${item.id}`}>
                {item.poster ? (
                  <img src={fullUrl(item.poster)} alt="video" className="w-full h-full object-cover" />
                ) : (
                  <video src={fullUrl(item.url)} className="w-full h-full object-cover" preload="metadata" muted />
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                  <Play size={32} weight="fill" className="text-white drop-shadow" />
                </div>
                <div className="absolute bottom-1 right-1 text-[9px] uppercase tracking-wider font-bold bg-black/70 text-sand-50 px-1.5 py-0.5 rounded">
                  Video · {item.duration ? `${item.duration.toFixed(1)}s` : ''}
                </div>
                <button type="button" onClick={() => deleteItem(item)}
                  className="absolute top-1 right-1 w-7 h-7 bg-white/90 hover:bg-white text-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`portfolio-delete-${item.id}`}>
                  <Trash size={12} />
                </button>
              </div>
            ))}
            {imgItems.map((item) => (
              <div key={item.id} className="relative aspect-square border border-[#E8E1D5] rounded overflow-hidden group bg-sand-100" data-testid={`portfolio-item-${item.id}`}>
                <img src={fullUrl(item.url)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => deleteItem(item)}
                  className="absolute top-1 right-1 w-7 h-7 bg-white/90 hover:bg-white text-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`portfolio-delete-${item.id}`}>
                  <Trash size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3 flex items-start gap-2 text-[11px] text-text-secondary">
          <Warning size={12} className="text-amber-600 shrink-0 mt-0.5" />
          <span>
            Rejected uploads include: text overlays, captions, subtitles, hand-written signs, large watermarks.
            Brand icon-marks (no words) and tiny domain watermarks are OK.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProPortfolioCard;

/**
 * GMapsImportRow — paste a Google Maps URL; server fetches listing photos
 * (via Places API, OCR-moderated) AND the latest reviews. Ownership is
 * enforced server-side: the listing's phone OR business name must match
 * the vendor's registered details, otherwise import is rejected.
 */
const GMapsImportRow = ({ onImported }) => {
  const [url, setUrl] = useState('');
  const [busyKind, setBusyKind] = useState(null); // 'photos' | 'reviews' | null

  const importPhotos = async () => {
    const trimmed = (url || '').trim();
    if (!trimmed) return;
    setBusyKind('photos');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/me/portfolio/import-gmaps`,
        { maps_url: trimmed, limit: 5 }, { withCredentials: true });
      const n = (data && data.imported) || 0;
      const skipped = ((data && data.skipped) || []).length;
      if (n > 0) {
        toast.success(skipped
          ? `Imported ${n} photo${n > 1 ? 's' : ''}, skipped ${skipped} with text overlay`
          : `Imported ${n} photo${n > 1 ? 's' : ''} from Google Maps`);
      } else {
        toast.info('No photos imported');
      }
      setUrl('');
      onImported && onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Google Maps import failed');
    }
    setBusyKind(null);
  };

  const importReviews = async () => {
    const trimmed = (url || '').trim();
    if (!trimmed) return;
    setBusyKind('reviews');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/me/portfolio/import-gmaps-reviews`,
        { maps_url: trimmed }, { withCredentials: true });
      const n = (data && data.imported) || 0;
      const place = data?.summary?.place_name;
      toast.success(n > 0
        ? `Imported ${n} review${n > 1 ? 's' : ''}${place ? ` from ${place}` : ''}`
        : 'No reviews available on that listing yet');
      onImported && onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reviews import failed');
    }
    setBusyKind(null);
  };

  const disabled = !!busyKind || !(url || '').trim();

  return (
    <div className="mb-4 p-3 border border-emerald-200 bg-emerald-50/40 rounded space-y-2"
      data-testid="portfolio-gmaps-row">
      <div className="text-[11px] text-text-secondary">
        Paste your Google Maps listing URL — we&apos;ll verify the phone <em>or</em> business name matches yours before pulling photos & reviews.
      </div>
      <div className="flex items-stretch gap-2">
        <div className="flex-1 relative">
          <MapPin size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" weight="fill" />
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://maps.app.goo.gl/… or google.com/maps/place/…"
            className="w-full pl-7 pr-2 h-9 border border-[#E8E1D5] rounded-none text-xs"
            data-testid="portfolio-gmaps-input"
          />
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button onClick={importPhotos} disabled={disabled}
          variant="outline"
          className="rounded-none border-emerald-400 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
          data-testid="portfolio-gmaps-photos-btn">
          {busyKind === 'photos'
            ? <Spinner size={14} className="mr-2 animate-spin" />
            : <MapPin size={14} className="mr-2" weight="fill" />}
          Import Photos
        </Button>
        <Button onClick={importReviews} disabled={disabled}
          variant="outline"
          className="rounded-none border-amber-400 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
          data-testid="portfolio-gmaps-reviews-btn">
          {busyKind === 'reviews'
            ? <Spinner size={14} className="mr-2 animate-spin" />
            : <Star size={14} className="mr-2" weight="fill" />}
          Import Reviews (latest 10)
        </Button>
      </div>
    </div>
  );
};
